import { GetVideosResponse } from '@eih-netbroker/api-erste-market-go';
import { useAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { ErsteMarketGoResource, VideosRequest } from '../../resources';
import { ersteMarketMediaHubAtom, ErsteMarketMediaHubAtomState } from '../../state';
import { HighlightedVideo, Video } from '../../types';
import { Loadable } from '../../utils';
import { GetResourceHeader } from '../useResource';

export type UseErsteMarketMediaHubCommonParams = {
  ersteMarketGoResource: ErsteMarketGoResource;
  getResourceHeader: GetResourceHeader;
};

export type UseErsteMarketMediaHubCommonOutput = {
  videos: ErsteMarketMediaHubAtomState['videos'];
  highlightedVideoDetail: HighlightedVideo | undefined;
  loadVideos: () => Promise<void>;
  loadMoreVideos: () => Promise<void>;
};

const PAGE_SIZE = 20;
const FIRST_PAGE = 1;

export const useErsteMarketMediaHubCommon = ({
  ersteMarketGoResource,
  getResourceHeader,
}: UseErsteMarketMediaHubCommonParams): UseErsteMarketMediaHubCommonOutput => {
  const [state, setState] = useAtom(ersteMarketMediaHubAtom);
  const isLoadingRef = useRef(false);

  const getVideos = useCallback(
    async (request?: VideosRequest): Promise<GetVideosResponse> => {
      const result = await ersteMarketGoResource.getVideos(request ?? {}, getResourceHeader());
      return result.data;
    },
    [ersteMarketGoResource, getResourceHeader],
  );

  const loadPage = useCallback(
    async (page: number): Promise<void> => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      setState(prev => ({
        ...prev,
        videos: Loadable.hasValue(prev.videos) ? Loadable.loadingWith(prev.videos) : Loadable.loading,
        ...(page === FIRST_PAGE && {
          highlightedVideo: Loadable.hasValue(prev.highlightedVideo)
            ? Loadable.loadingWith(prev.highlightedVideo)
            : Loadable.loading,
        }),
      }));

      try {
        const result = await getVideos({ page, limit: PAGE_SIZE });
        setState(prev => ({
          ...prev,
          videos: Loadable.loaded(
            page === FIRST_PAGE
              ? result.pagedVideoList
              : [...Loadable.getOrElse(prev.videos, [] as Video[]), ...result.pagedVideoList],
          ),
          ...(page === FIRST_PAGE && {
            highlightedVideo: Loadable.loaded(result.highlightedVideoDetail),
          }),
          currentPage: page,
          hasMore: result.pagedVideoList.length === PAGE_SIZE,
        }));
      } catch (e) {
        setState(prev => ({
          ...prev,
          videos: page === FIRST_PAGE ? Loadable.failed : prev.videos,
          ...(page === FIRST_PAGE && { highlightedVideo: Loadable.failed }),
        }));
      } finally {
        isLoadingRef.current = false;
      }
    },
    [getVideos, setState],
  );

  const loadVideos = useCallback((): Promise<void> => loadPage(FIRST_PAGE), [loadPage]);

  const loadMoreVideos = useCallback((): Promise<void> => {
    if (!state.hasMore) return Promise.resolve();
    return loadPage(state.currentPage + 1);
  }, [state.hasMore, state.currentPage, loadPage]);

  return {
    videos: state.videos,
    highlightedVideoDetail: Loadable.getOrElse(state.highlightedVideo, undefined),
    loadVideos,
    loadMoreVideos,
  };
};
